import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const STATUS_LABELS = {
  pending: 'Request Received',
  approved: 'Order Approved',
  payment_sent: 'Payment Requested',
  paid: 'Payment Confirmed',
  photo_uploaded: 'Arrangement Ready',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Hand Delivered'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  approved: 'bg-blue-50 text-blue-700 border-blue-100',
  payment_sent: 'bg-orange-50 text-orange-700 border-orange-100',
  paid: 'bg-green-50 text-green-700 border-green-100',
  photo_uploaded: 'bg-purple-50 text-purple-700 border-purple-100',
  out_for_delivery: 'bg-teal-50 text-teal-700 border-teal-100',
  delivered: 'bg-sage-50 text-sage-700 border-sage-100',
};

export default function CustomerDetailView({ customer, onBack, onSelectOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer?.id) {
      fetchCustomerOrders();
    }
  }, [customer?.id]);

  async function fetchCustomerOrders() {
    setLoading(true);
    if (isSupabaseConfigured() && customer?.id) {
      try {
        const { data, error } = await supabase
          .from('orders')
          .select('*, order_items(*)')
          .eq('customer_id', customer.id)
          .order('created_at', { ascending: false });

        if (!error && data) {
          setOrders(data);
        }
      } catch (err) {
        console.error("Error fetching customer orders:", err);
      }
    }
    setLoading(false);
  }

  const totalSpent = orders.reduce((sum, order) => {
    const itemTotal = order.order_items?.reduce((s, i) => s + (Number(i.price) || 0), 0) || 0;
    return sum + itemTotal;
  }, 0);

  return (
    <div className="space-y-8">
      <button onClick={onBack} className="btn-secondary">
        <SafeIcon name="ArrowLeft" /> Back to Customer List
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-[2rem] p-8 border border-sage-100 shadow-soft text-center">
            <div className="w-24 h-24 rounded-full bg-blush-100 flex items-center justify-center text-blush-500 text-3xl font-bold mx-auto mb-4">
              {customer.full_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <h2 className="text-2xl font-serif text-charcoal mb-1">{customer.full_name || 'No name'}</h2>
            <p className="text-sage-400 text-sm mb-6">{customer.email}</p>

            <div className="grid grid-cols-2 gap-4 pt-6 border-t border-sage-50">
              <div className="text-left">
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Member Since</p>
                <p className="text-sm font-bold text-charcoal">{new Date(customer.created_at).toLocaleDateString()}</p>
              </div>
              <div className="text-left">
                <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Total Spent</p>
                <p className="text-sm font-bold text-blush-500">${totalSpent.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] p-8 border border-sage-100 shadow-soft space-y-4">
            <h3 className="font-serif text-lg border-b border-sage-50 pb-2">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <SafeIcon name="Mail" className="text-blush-300 shrink-0" />
                <a href={`mailto:${customer.email}`} className="text-charcoal font-medium hover:text-blush-400 transition-colors">
                  {customer.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <SafeIcon name="Phone" className="text-blush-300 shrink-0" />
                <span className="text-charcoal font-medium">{customer.phone || 'No phone provided'}</span>
              </div>
            </div>
            <a
              href={`mailto:${customer.email}?subject=Your Phoenix Kabob Order`}
              className="btn-secondary w-full flex items-center justify-center gap-2 mt-2"
            >
              <SafeIcon name="Mail" /> Email Customer
            </a>
          </div>
        </div>

        {/* Orders Table */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="font-serif text-2xl text-charcoal">Order History</h3>
            <span className="bg-sage-50 text-sage-500 px-4 py-1 rounded-full text-[10px] font-bold uppercase border border-sage-100">
              {orders.length} {orders.length === 1 ? 'Order' : 'Orders'}
            </span>
          </div>

          <div className="bg-white rounded-[2.5rem] shadow-soft border border-sage-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-sage-50 border-b border-sage-100 text-[10px] text-sage-600 uppercase tracking-widest font-bold">
                    <th className="p-6">Order ID</th>
                    <th className="p-6">Delivery Date</th>
                    <th className="p-6">Status</th>
                    <th className="p-6 text-right">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sage-100">
                  {orders.map(order => {
                    const total = order.order_items?.reduce((s, i) => s + (Number(i.price) || 0), 0) || 0;
                    return (
                      <tr
                        key={order.id}
                        onClick={() => onSelectOrder(order)}
                        className="hover:bg-warmwhite transition-colors cursor-pointer group"
                      >
                        <td className="p-6 font-bold text-charcoal text-sm group-hover:text-blush-500">
                          #{order.id.substring(0, 8)}
                        </td>
                        <td className="p-6 text-sm text-sage-500">
                          {new Date(order.delivery_date).toLocaleDateString()}
                        </td>
                        <td className="p-6">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[order.status] || 'bg-sage-50 text-sage-600 border-sage-100'}`}>
                            {STATUS_LABELS[order.status] || order.status}
                          </span>
                        </td>
                        <td className="p-6 text-right font-bold text-charcoal">
                          ${total.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && orders.length === 0 && (
                    <tr>
                      <td colSpan="4" className="p-12 text-center text-sage-400 italic">No orders found for this customer.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {loading && <div className="p-12 text-center text-sage-400 font-bold uppercase tracking-widest text-xs">Loading orders...</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
